import { MediaType } from '@qse/api'
import { useCallback, useRef, useState } from 'react'

export interface FileItem {
  id: string | number
  name: string
  type: string
  lastModified: string
  size?: number // in bytes
  path?: string
  parentId?: string // Parent folder ID for hierarchical structure
  isFavorite?: boolean
  isDir?: boolean
  createdAt: string
  trashedAt?: string | null // When the file was moved to trash
  mimeType: string
  status?: string // File status (e.g., 'COMPLETED', 'UPLOADING', 'PROCESSING', 'ENCRYPTING', 'AVAILABLE')
  mediaType?: MediaType
  isEncrypted?: boolean // Whether the file is encrypted and ready for use
  systemFolderType?: 'CHAT_ROOT' | 'CHAT_CONVERSATION'
}

export interface Position {
  x: number
  y: number
}

export interface ElementBounds {
  left: number
  top: number
  right: number
  bottom: number
}

export interface MarqueeRect {
  left: number
  top: number
  right: number
  bottom: number
}

export interface ContextMenuState {
  isOpen: boolean
  position: Position
  fileId: number | string | null
  path?: string | null
  submenuOpen: string | null
}

export function useFileManager(initialFiles: FileItem[]) {
  const [files, setFiles] = useState<FileItem[]>(initialFiles)
  const [selectedFiles, setSelectedFiles] = useState<Set<string | number>>(new Set())
  const [draggedFiles, setDraggedFiles] = useState<(string | number)[]>([])
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)
  const [isMarqueeSelecting, setIsMarqueeSelecting] = useState(false)
  const [marqueeStart, setMarqueeStart] = useState<Position>({ x: 0, y: 0 })
  const [marqueeEnd, setMarqueeEnd] = useState<Position>({ x: 0, y: 0 })
  const [isDraggingFile, setIsDraggingFile] = useState(false)
  const [contextMenu, setContextMenu] = useState<ContextMenuState>({
    isOpen: false,
    position: { x: 0, y: 0 },
    fileId: null,
    path: null,
    submenuOpen: null,
  })

  const dragCounter = useRef(0)
  const fileRefs = useRef<Map<string | number, HTMLDivElement>>(new Map())
  const containerRef = useRef<HTMLDivElement>(null)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getElementBounds = (element: HTMLDivElement): ElementBounds => {
    const rect = element.getBoundingClientRect()
    const containerRect = containerRef.current!.getBoundingClientRect()

    return {
      left: rect.left - containerRect.left + containerRef.current!.scrollLeft,
      top: rect.top - containerRect.top + containerRef.current!.scrollTop,
      right: rect.right - containerRect.left + containerRef.current!.scrollLeft,
      bottom: rect.bottom - containerRect.top + containerRef.current!.scrollTop,
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isElementInMarquee = (elementBounds: ElementBounds, marqueeRect: MarqueeRect) => {
    return !(
      elementBounds.right < marqueeRect.left ||
      elementBounds.left > marqueeRect.right ||
      elementBounds.bottom < marqueeRect.top ||
      elementBounds.top > marqueeRect.bottom
    )
  }

  const updateMarqueeSelection = useCallback(() => {
    // This function is now a no-op for selection updates
    // Selection will be handled separately on mouseup
  }, [])

  const finalizeMarqueeSelection = useCallback(
    (trashMode = false) => {
      if (!isMarqueeSelecting) return

      const marqueeRect: MarqueeRect = {
        left: Math.min(marqueeStart.x, marqueeEnd.x),
        top: Math.min(marqueeStart.y, marqueeEnd.y),
        right: Math.max(marqueeStart.x, marqueeEnd.x),
        bottom: Math.max(marqueeStart.y, marqueeEnd.y),
      }

      const newSelected = new Set<string | number>()

      files.forEach(file => {
        const fileElement = fileRefs.current.get(file.id)

        if (fileElement) {
          const bounds = getElementBounds(fileElement)

          if (isElementInMarquee(bounds, marqueeRect)) {
            // Exclude CHAT_ROOT from selection in home mode, allow in trash mode
            if (file.systemFolderType && !trashMode) {
              return
            }
            newSelected.add(file.id)
          }
        }
      })

      setSelectedFiles(newSelected)
      setIsMarqueeSelecting(false)
    },
    [files, marqueeEnd, marqueeStart, isMarqueeSelecting, getElementBounds, isElementInMarquee],
  )

  const resetFileSelection = useCallback(() => {
    setSelectedFiles(new Set<string | number>())
    setDraggedFiles([])
    setDragOverIndex(null)
    setIsMarqueeSelecting(false)
    setMarqueeStart({ x: 0, y: 0 })
    setMarqueeEnd({ x: 0, y: 0 })
    setIsDraggingFile(false)
    setContextMenu({
      isOpen: false,
      position: { x: 0, y: 0 },
      fileId: null,
      path: null,
      submenuOpen: null,
    })
  }, [])

  return {
    files,
    setFiles,
    selectedFiles,
    setSelectedFiles,
    draggedFiles,
    setDraggedFiles,
    dragOverIndex,
    setDragOverIndex,
    isMarqueeSelecting,
    setIsMarqueeSelecting,
    marqueeStart,
    setMarqueeStart,
    marqueeEnd,
    setMarqueeEnd,
    isDraggingFile,
    setIsDraggingFile,
    contextMenu,
    setContextMenu,
    dragCounter,
    fileRefs,
    containerRef,
    getElementBounds,
    isElementInMarquee,
    updateMarqueeSelection,
    finalizeMarqueeSelection,
    resetFileSelection,
  }
}
